import { css } from '@emotion/react'
import React from 'react'
import Chef from '../assets/chef.svg'
import Bake from '../assets/oven.svg'
import Pan from '../assets/pan.svg'
import PressureCook from '../assets/pressurecooker.svg'
import Proteins from '../assets/proteins.svg'
import Salad from '../assets/salad.svg'
import Steam from '../assets/steam.svg'
import Vegetables from '../assets/vegetables.svg'
import {
  BakingSheet,
  FoodProcessor,
  InstantPot,
  MeatThermometer,
  SautePan,
  SiliconeTongs,
} from '../components/affiliates/Cooking'
import { Footnote } from '../components/curriculum/Footnote'
import { Divider } from '../components/divider/Divider'
import { Explanation } from '../components/explanation/Explanation'
import { Image } from '../components/image/Image'
import { Layout } from '../components/Layout'
import { Tabs } from '../components/tabs/Tabs'

export const WhyBakingProtein: React.FC<{ type: string }> = ({ type }) => (
  <Explanation color="blue">
    <div>
      <strong>Why baking?</strong> While baking takes more time, than say,
      sauteing, it is arguably the lowest-effort technique of the bunch. You
      just brush the {type} with some oil, stick it on a baking sheet in the
      oven, and wait 15 to 45 minutes.
    </div>
  </Explanation>
)

export const BakingEquipment = () => (
  <Explanation color="green">
    <div>
      <strong>Equipment: </strong>If you can afford it, a $15 stainless steel
      baking sheet, <BakingSheet text="like this one" />, is an excellent
      investment, as it will last forever and you can dishwash it, unlike an
      aluminum one.
    </div>
  </Explanation>
)

export const PressureCookerEquipment = () => (
  <Explanation color="green">
    <div>
      <strong>Equipment: </strong>The gold-standard pressure cooker is the{' '}
      <InstantPot />, for good reason. For $80, you get a reliable and safe
      pressure cooker. What more could you ask for?
    </div>
  </Explanation>
)

export const WhySalads = () => (
  <Explanation color="blue">
    <div>
      <strong>Why salads? </strong> Salads are the easiest way to prepare
      vegetables. You don{`'`}t even need to cook them! Plus, salads tend to
      involve <strong>leafy greens</strong>, the healthiest and lowest-calorie
      variant of vegetables.
    </div>
  </Explanation>
)

export const WhySteaming = () => (
  <Explanation color="blue">
    <div>
      <strong>Why steaming? </strong> Steaming is another super easy way to
      prepare vegetables. You just need to grab some vegetables, throw them in a
      steamer basket, put that basket in a pressure cooker with some water,
      click Steam, and wait 20 minutes.
    </div>
  </Explanation>
)

export const WhySauteing = () => (
  <Explanation color="blue">
    <div>
      <strong>Why sauteing?</strong> With a pan, oil, and some heat, you can
      cook <strong>thin</strong> slices of meat in under 10 minutes.
    </div>
  </Explanation>
)

export const SauteingEquipment = () => (
  <Explanation color="green">
    <div>
      <strong>Equipment: </strong>If you can afford it, a stainless steel
      tri-ply saute plan, <SautePan text="like this one" />, is an excellent
      investment. Otherwise, any saute pan or cast iron skillet will do.
    </div>
  </Explanation>
)

export const WhyPressureCooking = () => (
  <Explanation color="blue">
    <div>
      <strong>Why pressure cooking?</strong> With a pressure cooker, like the
      Instant Pot, you can perfectly cook meat{' '}
      <strong>directly from the freezer</strong>. All you need to do is pull the
      meat from the freezer, season it, dump it in the pressure cooker, hit a
      button, and wait. For example, it should take 15 minutes to make frozen
      chicken breast and 90 minutes to make an entire frozen turkey breast.
    </div>
  </Explanation>
)

type Group = 'Vegetables' | 'Protein'
type ProteinSubGroup = 'Saute' | 'Bake' | 'Pressure' | 'Tips'
type VegetableSubGroup = 'Bake' | 'Steam' | 'Salad' | 'Tips'

const tabs = css`
  button {
    height: 60px;
    margin-bottom: 10px;
    div {
      font-size: 1.1rem;
    }
    img {
      height: 30px;
      margin-left: 20px;
      width: 30px;
    }
  }
`

const Cooking = () => {
  const [group, setGroup] = React.useState('Vegetables' as Group)
  const [proteinSubGroup, setProteinSubGroup] = React.useState(
    'Saute' as ProteinSubGroup
  )
  const [vegetableSubGroup, setVegetableSubGroup] = React.useState(
    'Bake' as VegetableSubGroup
  )

  const isTip =
    (group === 'Vegetables' && vegetableSubGroup === 'Tips') ||
    (group === 'Protein' && proteinSubGroup === 'Tips')

  return (
    <Layout>
      <div className={'w90 center module mb50'}>
        <h1 className="huge">Cooking</h1>
        <Explanation color="blue">
          <div>
            <strong>Why:</strong> Learn to prepare vegetables and lean protein
            with little effort.
          </div>
        </Explanation>

        <Tabs
          className="mt20"
          styles={tabs}
          tabs={{
            Protein: {
              image: Proteins,
              title: 'Protein',
            },
            Vegetables: {
              image: Vegetables,
              title: 'Vegetables',
            },
          }}
          selected={group}
          onSelect={(tab: string | null) => {
            setGroup(tab as Group)
          }}
        />

        {group === 'Protein' && (
          <Tabs
            className="mt20"
            styles={tabs}
            tabs={{
              Bake: {
                image: Bake,
                title: 'Bake',
              },
              Pressure: {
                image: PressureCook,
                title: 'Pressure',
              },
              Saute: {
                image: Pan,
                title: 'Saute',
              },
              Tips: {
                image: Chef,
                title: 'Tips',
              },
            }}
            selected={proteinSubGroup}
            onSelect={(tab: string | null) => {
              setProteinSubGroup(tab as ProteinSubGroup)
            }}
          />
        )}

        {group === 'Vegetables' && (
          <Tabs
            className="mt20"
            styles={tabs}
            tabs={{
              Bake: {
                image: Bake,
                title: 'Bake',
              },
              Salad: {
                image: Salad,
                title: 'Salad',
              },
              Steam: {
                image: Steam,
                title: 'Steam',
              },
              Tips: {
                image: Chef,
                title: 'Tips',
              },
            }}
            selected={vegetableSubGroup}
            onSelect={(tab: string | null) => {
              setVegetableSubGroup(tab as VegetableSubGroup)
            }}
          />
        )}
        <div>
          {group === 'Vegetables' && (
            <div>
              {/* Baking */}
              {vegetableSubGroup === 'Bake' && (
                <div>
                  <div className="fcs mt50">
                    <Image width={100} height={100} alt="Oven" src={Bake} />
                    <h2 className="mt30 mb30">Bake</h2>
                  </div>
                  <Explanation color="blue">
                    <div>
                      <strong>Why baking?</strong> Baking is an extremely
                      low-effort way to prepare vegetables. And while it takes
                      twice as long as steaming, the resulting vegetables will
                      taste better because they will caramelize in the oven.
                      Yum!
                    </div>
                  </Explanation>

                  <BakingEquipment />
                  <Divider className="mt50 mb50" height={4} />
                  <h3 className="mt20 tcenter center">Recipes</h3>
                  <ul>
                    <li>
                      <a href="https://www.cookingclassy.com/roasted-vegetables/">
                        How to bake any vegetable
                      </a>
                    </li>
                  </ul>
                </div>
              )}

              {/* Steaming */}
              {vegetableSubGroup === 'Steam' && (
                <div>
                  <div className="fcs mt50">
                    <Image width={100} height={100} alt="Steamer" src={Steam} />
                    <h2 className="mt30 mb30">Steam</h2>
                  </div>
                  <WhySteaming />
                  <PressureCookerEquipment />
                  <Divider className="mt50 mb50" height={4} />
                  <h3 className="mt20 tcenter center">Recipes</h3>
                  <ul>
                    <li>
                      <a href="https://eatinginstantly.com/instant-pot-steamed-vegetables/">
                        How to steam any vegetable
                      </a>
                    </li>
                  </ul>
                </div>
              )}

              {/* Salad */}
              {vegetableSubGroup === 'Salad' && (
                <div>
                  <div className="fcs mt50">
                    <Image width={100} height={100} alt="Salads" src={Salad} />
                    <h2 className="mt30 mb30">Salad</h2>
                  </div>
                  <WhySalads />
                  <Explanation color="green">
                    <div>
                      <strong>Want to level up? </strong> Read this{' '}
                      <a href="https://cooking.nytimes.com/guides/43-how-to-make-salad">
                        excellent, short guide
                      </a>{' '}
                      on how to make salad.
                    </div>
                  </Explanation>
                  <Divider className="mt50 mb50" height={4} />
                  <h3 className="mt20 tcenter center">Recipes</h3>
                  <ul>
                    <li>
                      <a href="https://www.splendidtable.org/story/2019/06/20/simplest-green-salad">
                        Green salad
                      </a>
                    </li>
                    <li>
                      <a href="https://www.tapgenes.com/blog/put-salad-instead-dressing/">
                        Low-calorie alternatives to salad dressing
                      </a>
                    </li>
                  </ul>
                </div>
              )}
            </div>

            // End of vegetables :)
          )}

          {group === 'Protein' && (
            <div>
              {/* Saute */}
              {proteinSubGroup === 'Saute' && (
                <div>
                  <div className="fcs mt50">
                    <Image width={100} height={100} alt="Pan" src={Pan} />
                    <h2 className="mt30 mb30">Saute</h2>
                  </div>
                  <WhySauteing />
                  <SauteingEquipment />
                  <Explanation color="yellow">
                    <div>
                      <strong>Want to level up? </strong> Read this{' '}
                      <a href="https://www.reluctantgourmet.com/the-secret-to-great-saute/">
                        excellent, short post
                      </a>{' '}
                      on how to saute food.
                    </div>
                  </Explanation>
                  <Divider className="mt50 mb50" height={4} />
                  <h3 className="mt20 tcenter center">Recipes</h3>
                  <ul>
                    <li>
                      <a href="https://whatsinthepan.com/easy-pan-seared-pork-chops/">
                        Pork chops
                      </a>
                    </li>
                    <li>
                      <a href="https://www.myrecipes.com/recipe/sauted-chicken-breasts">
                        Chicken breast
                      </a>
                    </li>
                    <li>
                      <a href="https://www.simplyrecipes.com/recipes/how_to_cook_skirt_steak/">
                        Steak
                      </a>
                    </li>
                    <li>
                      <a href="https://skinnyms.com/simply-sauted-lemon-tilapia/">
                        Tilapia
                      </a>
                    </li>
                    <li>
                      <a href="https://thelemonbowl.com/easy-pan-seared-salmon/">
                        Salmon
                      </a>
                    </li>
                  </ul>
                </div>
              )}

              {/* Bake */}
              {proteinSubGroup === 'Bake' && (
                <div>
                  <div className="fcs mt50">
                    <Image width={100} height={100} alt="Oven" src={Bake} />
                    <h2 className="mt30 mb30">Bake</h2>
                  </div>
                  <WhyBakingProtein type="protein" />
                  <BakingEquipment />
                  <Divider className="mt50 mb50" height={4} />
                  <h3 className="mt20 tcenter center">Recipes</h3>
                  <ul>
                    <li>
                      <a href="https://www.spendwithpennies.com/oven-baked-chicken-breasts/">
                        Chicken breast
                      </a>
                    </li>
                    <li>
                      <a href="https://loseweightbyeating.com/oven-baked-pork-chops-recipes/">
                        Pork chops
                      </a>
                    </li>
                    <li>
                      <a href="https://simple-nourished-living.com/skinny-low-fat-calorie-healthy-tilapia-recipes-smart-points-plus-weight-watchers/">
                        Tilapia
                      </a>
                    </li>
                    <li>
                      <a href="https://www.gimmesomeoven.com/baked-salmon-recipe/">
                        Salmon
                      </a>
                    </li>
                  </ul>
                </div>
              )}

              {/* Pressure */}
              {proteinSubGroup === 'Pressure' && (
                <div>
                  <div className="fcs mt50">
                    <Image
                      width={100}
                      height={100}
                      alt="Pressure Cooker"
                      src={PressureCook}
                    />
                    <h2 className="mt30 mb30">Pressure</h2>
                  </div>
                  <WhyPressureCooking />
                  <PressureCookerEquipment />
                  <Explanation color="yellow">
                    <div>
                      <strong>Want to level up? </strong> Read this{' '}
                      <a href="https://cooking.nytimes.com/guides/46-how-to-use-an-instant-pot">
                        excellent, short guide
                      </a>{' '}
                      on the many uses of the Instant Pot.
                    </div>
                  </Explanation>
                  <Divider className="mt50 mb50" height={4} />
                  <h3 className="mt20 tcenter center">Recipes</h3>
                  <ul>
                    <li>
                      <a href="https://www.crunchycreamysweet.com/instant-pot-boneless-turkey-breast/">
                        Whole turkey
                      </a>
                    </li>
                    <li>
                      <a href="https://40aprons.com/instant-pot-frozen-chicken-breast/">
                        Chicken breast
                      </a>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
        {isTip && (
          <div>
            <div className="fcs mt50">
              <Image width={100} height={100} alt="Chef" src={Chef} />
              <h2 className="mt30 mb30">Tips</h2>
            </div>
            <Explanation color="blue">
              <div>
                <strong>Tip:</strong> Many of these recipes call for an oil. We
                usually recommend <strong>canola oil</strong> for its high smoke
                point{' '}
                <Footnote link="https://www.seriouseats.com/2014/05/cooking-fats-101-whats-a-smoke-point-and-why-does-it-matter.html" />
                , and because it is cheap and healthy{' '}
                <Footnote link="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC3746113" />
                .
              </div>
            </Explanation>
            <Explanation color="green" className="pb10">
              <div>
                <strong>Equipment:</strong> If you can afford it, it{`'`}s worth
                adding the following to your kitchen:
                <ul>
                  <li>
                    <SiliconeTongs /> for $15. Makes it easy to stir and flip
                    everything, especially thin cuts of meat.
                  </li>
                  <li>
                    <MeatThermometer /> for $20. Makes it easy to tell when meat
                    is done.
                  </li>
                  <li>
                    <FoodProcessor /> for $50. Makes cutting and shredding
                    vegetables insanely easy.
                  </li>
                </ul>
              </div>
            </Explanation>
          </div>
        )}
      </div>
    </Layout>
  )
}

export default Cooking
